/**
 * Settles: how a body comes to rest, instead of cutting to its standing frame.
 *
 * A body that stops walking or running is caught somewhere in its stride; one that has struck a
 * blow is left in its fighting stance. A settle is a short clip from that pose into the standing
 * one: a foot off its spot steps in (the standing one follows if it is far from its own), the hips
 * rise out of the stride's crouch, the chest rocks and comes upright, and the arms carry on a
 * little with the swing they had and come to rest. Each of those is a damped spring, so the motion
 * keeps the momentum it had and lands without a bounce.
 *
 * And the other way: a blow struck from standing eases its first frames out of the standing pose,
 * the lead foot stepping into the stance, without changing when the blow lands.
 */
import { EmberClip, EmberKey, sampleKeyframes } from "./clips"
import { ArmPose, GRIP_OUT, LegTarget, PoseSample, Proportions, SIDES, Side, Skeleton, WRIST_REST, maxPelvisHeight, solvePose, standingPelvisHeight } from "./rig"
import { Vec3, add, apply, dot, ease, length, lerp, normalize, rotY, scale, smoothstep, sub, transpose } from "./vec"

/** The action a settle's name starts with: `settle.walk.5-left` (from stride frame 5), `settle.slash-left`. */
export const SETTLE = "settle"
/** The action a start's name starts with: `start.run-left` (setting off into a run from standing). */
export const START = "start"
/**
 * The action a gait change's name starts with: `shift.run.1-left` -- out of a run into a walk (or a
 * walk into a run), from its first (0) or second (1) heel strike.
 */
export const SHIFT = "shift"
/** What a blow struck from standing adds to its name: `slash.rest-left`. */
export const FROM_REST = "rest"
/** Attacks struck from a stance with the lead foot forward: eased into from standing and settled out of. */
export const STANCE_ATTACKS = ["slash", "thrust"]
export const SETTLE_FRAME_MS = 50

export interface SettleTuning {
    /** How long a settle takes with one step, and with two (the standing foot following), in seconds. */
    oneStep: number
    twoSteps: number
    /** A standing foot further than this from its resting place steps too, rather than sliding. */
    slideMax: number
    /** How high a stepping foot lifts, and how far the hips give as the weight comes down. */
    lift: number
    dip: number
    /** Share of the arms' swing speed carried into the settle. */
    armMomentum: number
    /** Forward pitch a sudden stop throws into the chest (rad/s). */
    leanKick: number
    /** Period (s) and damping ratio of the springs the arms, chest and hips come to rest on. */
    period: number
    damping: number
    /** Caught in the air: the share of the settle the foot about to land takes coming down. */
    landing: number
}

const WALK_SETTLE: SettleTuning = {
    oneStep: 0.3,
    twoSteps: 0.42,
    slideMax: 1.6,
    lift: 1.4,
    dip: 0.35,
    armMomentum: 0.55,
    leanKick: 0.5,
    period: 0.4,
    damping: 0.45,
    landing: 0.12,
}

/**
 * By the stride it stops: a run carries more and settles longer. It brakes: the standing foot skids
 * home under the weight rather than stepping, the knees sink deep, the chest is thrown back against
 * the run's lean (past upright, held to MAX_SWING_PAST) while the arms carry on forward, and then it
 * comes up.
 */
export const STRIDE_SETTLES: Record<string, SettleTuning> = {
    walk: WALK_SETTLE,
    run: { ...WALK_SETTLE, oneStep: 0.5, twoSteps: 0.6, slideMax: 3.5, lift: 1.8, dip: 1.6, armMomentum: 0.7, leanKick: -2.5, period: 0.5 },
}

/** Out of a blow: one step back for the lead foot, the arms coming down. */
export const STANCE_SETTLE: SettleTuning = { ...WALK_SETTLE, oneStep: 0.3, twoSteps: 0.3, lift: 1.2, dip: 0.3, armMomentum: 0, leanKick: 0 }

/** Ground speed (px/s) a stride's arms are taken to be swinging at as it settles: the player's walk and run. */
export const SETTLE_SPEED: Record<string, number> = { walk: 100, run: 160 }

/** Share of a blow struck from standing over which it eases out of the standing pose. */
export const EASE_IN = 0.3

/** An arm swinging back past its rest goes at most this far beyond it. */
const MAX_SWING_PAST = (8 * Math.PI) / 180

const mix = (a: number, b: number, u: number) => a + (b - a) * u

/** A damped spring's displacement `tau` seconds after leaving x0 at v0. */
const spring = (x0: number, v0: number, tau: number, t: SettleTuning) => {
    const omega = (2 * Math.PI) / t.period
    const wd = omega * Math.sqrt(1 - t.damping * t.damping)
    const e = Math.exp(-t.damping * omega * tau)
    return e * (x0 * Math.cos(wd * tau) + ((v0 + t.damping * omega * x0) / wd) * Math.sin(wd * tau))
}

/** Whatever of the spring is left fades out over the last third, so a settle ends exactly at rest. */
const fadeOut = (u: number) => 1 - smoothstep((u - 0.65) / 0.35)

/** `past` beyond the rest it swung through, held to MAX_SWING_PAST however hard it came. */
const capPast = (past: number) => Math.sign(past) * MAX_SWING_PAST * Math.tanh(Math.abs(past) / MAX_SWING_PAST)

/** An angle settling on `rest` from `from`, moving at `velocity` (rad/s) when it started. */
const settleAngle = (from: number, rest: number, velocity: number, tau: number, u: number, t: SettleTuning) => {
    const d = spring(from - rest, velocity, tau, t) * fadeOut(u)
    return rest + (Math.sign(d) !== Math.sign(from - rest) && d !== 0 ? capPast(d) : d)
}

const leg = (pose: PoseSample, side: Side) => pose.legs[side] as LegTarget
const allIk = (...poses: PoseSample[]) => poses.every(pose => SIDES.every(s => pose.legs[s].mode === "ik"))
const offFrom = (a: LegTarget, b: LegTarget) => Math.hypot(a.ankle[0] - b.ankle[0], a.ankle[2] - b.ankle[2])

/** A foot `s` (0..1) of the way through a step from `a` to `b`, lifted `lift` at its height. */
const stepTo = (a: LegTarget, b: LegTarget, s: number, lift: number): LegTarget => {
    const c = Math.min(1, Math.max(0, s))
    const e = ease(c)
    const up = c > 0 && c < 1 ? lift * Math.sin(Math.PI * c) : 0
    const ankle: Vec3 = [mix(a.ankle[0], b.ankle[0], e), mix(a.ankle[1], b.ankle[1], e) + up, mix(a.ankle[2], b.ankle[2], e)]
    return { mode: "ik", ankle, footPitch: mix(a.footPitch, b.footPitch, e), planted: c <= 0 || c >= 1 }
}

/** Keep the pelvis low enough that every foot on the ground is still reached. */
const reachable = (pose: PoseSample, p: Proportions): PoseSample => {
    const sk = solvePose(pose, p)
    let height = pose.root[1]
    for (const s of SIDES) {
        const l = pose.legs[s]
        if (l.mode === "ik" && l.planted !== false) height = Math.min(height, maxPelvisHeight(l.ankle, [sk[s].hip[0], sk[s].hip[2]], p))
    }
    return height === pose.root[1] ? pose : { ...pose, root: [pose.root[0], height, pose.root[2]] }
}

/** The body's turn about the vertical (a blow's hips turn it; nothing a settle starts from rolls or pitches it). */
const yawOf = (pose: PoseSample) => Math.atan2(pose.rootRotation[2], pose.rootRotation[0])

/** The arm as a place for the hand -- from its shoulder, in the chest's frame -- as `pose` puts it. */
const reachOf = (pose: PoseSample, side: Side, p: Proportions, sk?: Skeleton): ArmPose => {
    const arm = pose.arms[side]
    if (arm.reach && arm.pole && arm.grip) return arm
    const solved = sk ?? solvePose(pose, p)
    const toChest = transpose(solved.torsoBasis)
    const limb = solved[side]
    const bend = normalize(sub(limb.elbow, scale(add(limb.shoulder, limb.hand), 0.5)))
    return {
        ...arm,
        reach: arm.reach ?? apply(toChest, sub(limb.hand, limb.shoulder)),
        pole: arm.pole ?? (dot(bend, bend) > 0.5 ? apply(toChest, bend) : [0, 0, -1]),
        grip: arm.grip ?? apply(toChest, limb.grip),
    }
}

/** Along the great circle from `a` toward `b`: `s` past 1 carries on beyond `b`. */
const slerpDirection = (a: Vec3, b: Vec3, s: number): Vec3 => {
    const ua = normalize(a), ub = normalize(b)
    const angle = Math.acos(Math.max(-1, Math.min(1, dot(ua, ub))))
    if (angle < 1e-4) return normalize(lerp(ua, ub, s))
    const k = Math.sin(angle)
    return add(scale(ua, Math.sin((1 - s) * angle) / k), scale(ub, Math.sin(s * angle) / k))
}

/**
 * An arm `s` of the way from how `a` holds it to how `b` does, `s` past 1 swinging on beyond `b`
 * (held to MAX_SWING_PAST). Angles mix as angles; an arm placed by its hand (a polearm carried, both
 * hands on a hilt, a load in the arms) moves its hand round the shoulder instead.
 */
const blendArm = (a: PoseSample, b: PoseSample, side: Side, s: number, p: Proportions, skA?: Skeleton, skB?: Skeleton): ArmPose => {
    const A = a.arms[side], B = b.arms[side]
    if (!A.reach && !B.reach) {
        const angle = (x: number, y: number) => (s > 1 ? y + capPast((s - 1) * (y - x)) : mix(x, y, s))
        return {
            pitch: angle(A.pitch, B.pitch),
            abduct: angle(A.abduct, B.abduct),
            flex: angle(A.flex, B.flex),
            wrist: mix(A.wrist ?? WRIST_REST, B.wrist ?? WRIST_REST, Math.min(1, s)),
            out: mix(A.out ?? GRIP_OUT, B.out ?? GRIP_OUT, Math.min(1, s)),
            open: s < 0.5 ? A.open : B.open,
            ...(A.protract || B.protract ? { protract: mix(A.protract ?? 0, B.protract ?? 0, Math.min(1, s)) } : {}),
        }
    }
    const ra = reachOf(a, side, p, skA), rb = reachOf(b, side, p, skB)
    const turn = Math.acos(Math.max(-1, Math.min(1, dot(normalize(ra.reach!), normalize(rb.reach!)))))
    const along = s > 1 && turn > 1e-4 ? 1 + Math.min(s - 1, MAX_SWING_PAST / turn) : s
    return {
        ...(s < 0.5 ? A : B),
        ...(A.protract || B.protract ? { protract: mix(A.protract ?? 0, B.protract ?? 0, Math.min(1, along)) } : {}),
        reach: scale(slerpDirection(ra.reach!, rb.reach!, along), mix(length(ra.reach!), length(rb.reach!), Math.min(1, along))),
        pole: normalize(lerp(ra.pole!, rb.pole!, Math.min(1, along))),
        grip: normalize(lerp(ra.grip!, rb.grip!, Math.min(1, along))),
    }
}

/**
 * Settle out of a stride caught at `phase`: the foot that is off the ground, or furthest from its
 * spot, steps in; a standing foot further than `slideMax` from its own steps after it, a nearer one
 * slides home under the weight. Caught in the air (a run's flight), the foot that would land next
 * comes down where it is first. The arms carry on with the swing they had (a share of it), the chest
 * pitches a little further with the sudden stop and rocks back upright.
 *
 * Built for one body and facing (how many frames it takes depends on where the feet were), so
 * sampling ignores the proportions and facing it is handed. Undefined for a stride or a standing
 * pose whose legs are not placed by their feet.
 */
export const settleFromStride = (
    gait: EmberClip,
    phase: number,
    cyclesPerSecond: number,
    rest: EmberClip,
    tuning: SettleTuning,
    p: Proportions,
    facing: string,
): EmberClip | undefined => {
    const from = gait.sample(phase, p, facing)
    const dt = 0.004
    const next = gait.sample((phase + dt * cyclesPerSecond) % 1, p, facing)
    const still = rest.sample(0, p, facing)
    if (!allIk(from, still)) return undefined
    const off = (s: Side) => offFrom(leg(from, s), leg(still, s))
    const planted = SIDES.filter(s => leg(from, s).planted)
    const landsIn = (s: Side) => {
        for (let k = 1; k <= 100; k++) if ((gait.sample((phase + k / 100) % 1, p, facing).legs[s] as LegTarget).planted) return k
        return Infinity
    }
    const landing = planted.length === 0
    const pivot = landing
        ? SIDES.slice().sort((a, b) => landsIn(a) - landsIn(b))[0]
        : planted.slice().sort((a, b) => off(a) - off(b))[0]
    const stepper: Side = pivot === "L" ? "R" : "L"
    const landed: LegTarget = { ...leg(from, pivot), ankle: [leg(from, pivot).ankle[0], leg(still, pivot).ankle[1], leg(from, pivot).ankle[2]], footPitch: 0 }
    const pivotStart = landing ? landed : leg(from, pivot)
    const slideFrom = landing ? tuning.landing + 0.1 : 0.15
    const twoSteps = off(pivot) > tuning.slideMax
    const duration = twoSteps ? tuning.twoSteps : tuning.oneStep
    const frames = Math.max(2, Math.round((duration * 1000) / SETTLE_FRAME_MS))
    const firstEnd = twoSteps ? 0.5 : 0.7
    const lift = (s: Side, share: number) => Math.min(tuning.lift, Math.max(0.5, off(s) * 0.12)) * share
    const velocity = (a: number, b: number) => (b - a) / dt
    const swinging = SIDES.every(s => !from.arms[s].reach && !still.arms[s].reach)
    const skFrom = solvePose(from, p), skStill = solvePose(still, p)

    const sample = (t: number): PoseSample => {
        // Frame 0 follows the stride frame on show; the last comes just before the standing frame.
        const u = (t * (frames - 1) + 1) / (frames + 1)
        const tau = u * duration
        const e = ease(u)
        const share = 1 - spring(1, 0, tau, tuning) * fadeOut(u)
        const legs = {} as PoseSample["legs"]
        legs[stepper] = stepTo(leg(from, stepper), leg(still, stepper), u / firstEnd, lift(stepper, 1))
        legs[pivot] =
            landing && u < tuning.landing
                ? stepTo(leg(from, pivot), landed, u / tuning.landing, 0)
                : twoSteps
                  ? stepTo(pivotStart, leg(still, pivot), (u - firstEnd) / (0.95 - firstEnd), lift(pivot, 0.7))
                  : { ...stepTo(pivotStart, leg(still, pivot), (u - slideFrom) / (0.85 - slideFrom), 0), planted: true }
        const arm = (s: Side): ArmPose => {
            if (!swinging) return blendArm(from, still, s, share, p, skFrom, skStill)
            const a = from.arms[s], b = still.arms[s], n = next.arms[s]
            return {
                pitch: settleAngle(a.pitch, b.pitch, velocity(a.pitch, n.pitch) * tuning.armMomentum, tau, u, tuning),
                abduct: mix(a.abduct, b.abduct, e),
                flex: settleAngle(a.flex, b.flex, velocity(a.flex, n.flex) * tuning.armMomentum, tau, u, tuning),
                wrist: mix(a.wrist ?? WRIST_REST, b.wrist ?? WRIST_REST, e),
                out: mix(a.out ?? GRIP_OUT, b.out ?? GRIP_OUT, e),
                // The hand opens as it comes to rest, mid-settle, while it still moves.
                open: e < 0.5 ? a.open : b.open,
                ...(a.protract || b.protract ? { protract: mix(a.protract ?? 0, b.protract ?? 0, e) } : {}),
            }
        }
        const lean = settleAngle(from.lean, still.lean, velocity(from.lean, next.lean) + tuning.leanKick, tau, u, tuning)
        return reachable(
            {
                root: [mix(from.root[0], still.root[0], e), mix(from.root[1], still.root[1], e) - tuning.dip * Math.sin(Math.PI * u), mix(from.root[2], still.root[2], e)],
                rootRotation: still.rootRotation,
                lean,
                twist: settleAngle(from.twist, still.twist, velocity(from.twist, next.twist), tau, u, tuning),
                sideBend: mix(from.sideBend, still.sideBend, e),
                // The eyes stay level as the chest rocks.
                headPitch: mix(from.headPitch, still.headPitch, e) - 0.4 * (lean - mix(from.lean, still.lean, e)),
                headYaw: mix(from.headYaw, still.headYaw, e),
                arms: { L: arm("L"), R: arm("R") },
                legs,
                draw: still.draw,
            },
            p,
        )
    }
    return { name: SETTLE, frames, frameMs: SETTLE_FRAME_MS, loop: false, sample }
}

/**
 * Settle out of the last pose of `end` (a blow struck standing): a foot out of its place steps back,
 * the chest comes upright and the arms come down -- arms raised above level coming down a little out
 * to the sides, not across the face -- swinging once past their rest.
 */
export const settleFromPose = (end: EmberClip, rest: EmberClip, p: Proportions, facing: string, tuning: SettleTuning = STANCE_SETTLE): EmberClip | undefined => {
    const from = end.sample(1, p, facing)
    const still = rest.sample(0, p, facing)
    if (!allIk(from, still)) return undefined
    const duration = tuning.oneStep
    const frames = Math.max(2, Math.round((duration * 1000) / SETTLE_FRAME_MS))
    const skFrom = solvePose(from, p), skStill = solvePose(still, p)
    const yaw = yawOf(from)

    const sample = (t: number): PoseSample => {
        const u = (t * (frames - 1) + 1) / (frames + 1)
        const tau = u * duration
        const e = ease(u)
        const share = 1 - spring(1, 0, tau, tuning) * fadeOut(u)
        const legs = {} as PoseSample["legs"]
        for (const s of SIDES) {
            legs[s] = offFrom(leg(from, s), leg(still, s)) > 0.5 ? stepTo(leg(from, s), leg(still, s), (u - 0.1) / 0.5, tuning.lift) : { ...leg(still, s), planted: true }
        }
        const sweep = (s: Side) => 0.45 * Math.max(0, from.arms[s].pitch - Math.PI / 2) * Math.sin(Math.PI * Math.min(1, u * 1.8))
        const arm = (s: Side): ArmPose => {
            const blended = blendArm(from, still, s, share, p, skFrom, skStill)
            return blended.reach ? blended : { ...blended, abduct: mix(from.arms[s].abduct, still.arms[s].abduct, e) + sweep(s) }
        }
        return reachable(
            {
                root: [mix(from.root[0], still.root[0], e), mix(from.root[1], still.root[1], e) - tuning.dip * Math.sin(Math.PI * u), mix(from.root[2], still.root[2], e)],
                rootRotation: yaw ? rotY(yaw * (1 - e)) : still.rootRotation,
                lean: mix(from.lean, still.lean, share),
                twist: mix(from.twist, still.twist, share),
                sideBend: mix(from.sideBend, still.sideBend, e),
                headPitch: mix(from.headPitch, still.headPitch, e),
                headYaw: mix(from.headYaw, still.headYaw, e),
                arms: { L: arm("L"), R: arm("R") },
                legs,
                draw: still.draw,
            },
            p,
        )
    }
    return { name: SETTLE, frames, frameMs: SETTLE_FRAME_MS, loop: false, sample }
}

/** The stance's lead foot: the one the blow puts furthest ahead of where it stands at rest. */
const leadFoot = (stance: PoseSample, still: PoseSample): Side | undefined => {
    const ahead = (s: Side) => leg(stance, s).ankle[2] - leg(still, s).ankle[2]
    const lead: Side = ahead("L") >= ahead("R") ? "L" : "R"
    return ahead(lead) > 1 ? lead : undefined
}

/**
 * A blow struck standing still: the lead foot steps forward into the stance and the other stays
 * where it stood, so one step out of the blow brings the body back to standing. `fromRest` eases the
 * first EASE_IN of it out of the standing pose -- the lead foot stepping in, the arms and chest
 * mixing from standing into the blow's own -- with the same frames and timing, so the blow lands
 * when it always did.
 */
export const standingAttack = (attack: EmberClip, rest: EmberClip, fromRest: boolean): EmberClip => ({
    ...attack,
    sample: (t, p, facing) => {
        const a = attack.sample(t, p, facing)
        const still = rest.sample(0, p, facing)
        const stance = attack.sample(0, p, facing)
        if (!allIk(a, still, stance)) return a
        const lead = leadFoot(stance, still)
        const into = (t + 0.1) / (EASE_IN + 0.1)
        const legs = {} as PoseSample["legs"]
        for (const s of SIDES) {
            legs[s] = s !== lead ? { ...leg(still, s), planted: true } : fromRest ? stepTo(leg(still, s), leg(a, s), into, STANCE_SETTLE.lift) : leg(a, s)
        }
        const w = fromRest ? smoothstep(into) : 1
        if (w >= 1) return reachable({ ...a, legs }, p)
        return reachable(
            {
                ...a,
                root: [mix(still.root[0], a.root[0], w), mix(still.root[1], a.root[1], w), mix(still.root[2], a.root[2], w)],
                lean: mix(still.lean, a.lean, w),
                twist: mix(still.twist, a.twist, w),
                sideBend: mix(still.sideBend, a.sideBend, w),
                headPitch: mix(still.headPitch, a.headPitch, w),
                headYaw: mix(still.headYaw, a.headYaw, w),
                arms: { L: blendArm(still, a, "L", w, p), R: blendArm(still, a, "R", w, p) },
                legs,
            },
            p,
        )
    },
})

/** A start hands over to its stride this many frames before the stride's end. */
const START_HANDOVER_BEFORE_END = 2

/** By the stride it sets off into: the extra lean (rad) of the first push, and how far (px) the hips drop into it. */
export interface StartDrive {
    lean: number
    dip: number
}
export const START_DRIVES: Record<string, StartDrive> = {
    walk: { lean: (2.5 * Math.PI) / 180, dip: 0.35 },
    run: { lean: (7 * Math.PI) / 180, dip: 0.9 },
}

export interface StartPlan {
    clip: EmberClip
    /** Where in the stride the start hands over: the stride plays on from here. */
    phaseAfter: number
    /** The share of the stride's cycle the start covers (its ground, for the pace). */
    cycles: number
}

/**
 * Set off from standing into `gait` (a walk or a run), the other way round from a settle: the start picks the stride
 * up where a foot is planted where it stands already (under the hips standing; in an archer's
 * stance, `rest` the bow held ready, a step ahead or behind) -- and plays on through the
 * stride at the stride's own pace, so that planted foot is carried back by the ground and never
 * slides. Over the first half the rest of the body comes out of the standing pose into the
 * stride's: the other foot lifts off from beside it into its swing, and the body tips forward and
 * drops into the first push (`drive`), leaning in further than the stride will, before it rises
 * into the stride.
 * Hands over to the stride a couple of frames before its end (`phaseAfter`).
 */
export const startIntoStride = (gait: EmberClip, rest: EmberClip, p: Proportions, facing: string, drive: StartDrive): StartPlan | undefined => {
    const still = rest.sample(0, p, facing)
    const first = gait.sample(0, p, facing)
    if (!allIk(first, still)) return undefined
    const n = gait.frames
    const end = n - START_HANDOVER_BEFORE_END
    // Where the standing body already is: a frame of the stride with a planted foot under the hips --
    // each foot has one; the one that leaves the longer way to the handover.
    const under = SIDES.map(side => {
        let at = 0
        let best = Infinity
        // Where that foot stands now (under the hips standing; a step ahead or behind in an archer's stance).
        const stands = leg(still, side).ankle[2]
        for (let i = 0; i < n; i++) {
            const l = leg(gait.sample(i / n, p, facing), side)
            if (l.planted && Math.abs(l.ankle[2] - stands) < best) {
                best = Math.abs(l.ankle[2] - stands)
                at = i
            }
        }
        return at
    })
    const spanFrom = (i: number) => ((end - i + n) % n) || n
    const from = spanFrom(under[0]) >= spanFrom(under[1]) ? under[0] : under[1]
    const frames = spanFrom(from)
    const phase0 = from / n
    const sk = solvePose(still, p)
    // Each foot keeps its standing place until it is in the air, then swings into the stride's track.
    const lifts = (side: Side) => {
        for (let i = 0; i < frames; i++) if (!leg(gait.sample((phase0 + i / n) % 1, p, facing), side).planted) return i / frames
        return 1
    }
    const lift = { L: lifts("L"), R: lifts("R") }
    const driveLean = drive.lean
    const turned = yawOf(still)
    const sample = (t: number): PoseSample => {
        const i = t * (frames - 1)
        const u = i / frames
        const g = gait.sample((phase0 + i / n) % 1, p, facing)
        // Out of the standing pose over the first half, tipping forward and dropping into the first push.
        const w = smoothstep(u / 0.55)
        const push = Math.sin(Math.PI * Math.min(1, u / 0.7))
        const legs = {} as PoseSample["legs"]
        for (const side of SIDES) {
            const a = leg(still, side), b = leg(g, side)
            const k = smoothstep((u - lift[side]) / 0.3)
            // Still down where it stood, the ground carries it back (z); in the air it eases into the stride.
            const down = lift[side] > 0 && u < lift[side]
            legs[side] = {
                ...b,
                ankle: down ? [a.ankle[0], b.ankle[1], b.ankle[2]] : [mix(a.ankle[0], b.ankle[0], k), mix(a.ankle[1], b.ankle[1], k), mix(lift[side] > 0 ? b.ankle[2] : a.ankle[2], b.ankle[2], k)],
                footPitch: down ? b.footPitch : mix(a.footPitch, b.footPitch, k),
            }
        }
        const lean = mix(still.lean, g.lean, w) + driveLean * push
        return reachable(
            {
                ...g,
                // A body standing turned (an archer side-on to the shot) unwinds into the stride's heading.
                rootRotation: turned ? rotY(mix(turned, yawOf(g), w)) : g.rootRotation,
                ...(still.draw ? { draw: still.draw * (1 - smoothstep(u / 0.3)) } : {}),
                root: [mix(still.root[0], g.root[0], w), mix(still.root[1], g.root[1], w) - drive.dip * push, mix(still.root[2], g.root[2], w)],
                lean,
                twist: mix(still.twist, g.twist, w),
                sideBend: mix(still.sideBend, g.sideBend, w),
                headPitch: mix(still.headPitch, g.headPitch, w),
                headYaw: mix(still.headYaw, g.headYaw, w),
                arms: { L: blendArm(still, g, "L", w, p, sk), R: blendArm(still, g, "R", w, p, sk) },
                legs,
            },
            p,
        )
    }
    return { clip: { name: START, frames, frameMs: gait.frameMs, loop: false, sample }, phaseAfter: (end % n) / n, cycles: frames / n }
}

/**
 * Change gait mid-stride, at a heel strike (`contact` 0: the left's, 1: the right's): over half a
 * cycle of `to`, the legs take up the new stride from that strike -- a foot planted in both stays on
 * the new stride's path, so the ground carries it at the new pace and it never slides; one that was
 * in the air (or down) in the old stride comes down into (or lifts off into) the new -- while the
 * rest of the body eases out of the old stride's carriage into the new one's: the lean, the arms,
 * the head and how low the hips ride.
 */
export const shiftStride = (from: EmberClip, to: EmberClip, contact: number, p: Proportions, facing: string): StartPlan | undefined => {
    if (!allIk(from.sample(0, p, facing), to.sample(0, p, facing))) return undefined
    const n = to.frames
    const frames = Math.round(n / 2)
    const phase0 = contact * 0.5
    const sample = (t: number): PoseSample => {
        const i = t * (frames - 1)
        const u = i / frames
        const q = (phase0 + i / n) % 1
        const a = from.sample(q, p, facing), b = to.sample(q, p, facing)
        const w = smoothstep(u / 0.85)
        const k = smoothstep(u / 0.5)
        const legs = {} as PoseSample["legs"]
        for (const side of SIDES) {
            const la = leg(a, side), lb = leg(b, side)
            legs[side] = la.planted && lb.planted ? lb : { ...lb, ankle: lerp(la.ankle, lb.ankle, k), footPitch: mix(la.footPitch, lb.footPitch, k) }
        }
        return reachable(
            {
                ...b,
                root: [mix(a.root[0], b.root[0], w), mix(a.root[1], b.root[1], w), b.root[2]],
                lean: mix(a.lean, b.lean, w),
                twist: mix(a.twist, b.twist, w),
                sideBend: mix(a.sideBend, b.sideBend, w),
                headPitch: mix(a.headPitch, b.headPitch, w),
                headYaw: mix(a.headYaw, b.headYaw, w),
                arms: { L: blendArm(a, b, "L", w, p), R: blendArm(a, b, "R", w, p) },
                legs,
            },
            p,
        )
    }
    return { clip: { name: SHIFT, frames, frameMs: to.frameMs, loop: false, sample }, phaseAfter: ((phase0 * n + frames) % n) / n, cycles: frames / n }
}

/** A leap on the move is drawn from this many points round its stride (`jump.run.2-left`: from its third quarter). */
export const LEAP_PHASES = 4

/** When a jump leaves the ground, holds its legs reaching down (a leap off an edge), lands; shares of it, and its length. */
export interface JumpTiming {
    takeoff: number
    hold: number
    landing: number
    ms: number
}

const deg = (d: number) => (d * Math.PI) / 180
const otherSide = (side: Side): Side => (side === "L" ? "R" : "L")

/** A stride's pose as a key: its feet placed where it has them (lifted off the ground or not), its arms by their angles. */
const keyOf = (pose: PoseSample, t: number, p: Proportions): EmberKey => {
    const arm = (a: ArmPose): [number, number, number, number] => [a.pitch, a.abduct, a.flex, a.wrist ?? WRIST_REST]
    const feet = (side: Side): [number, number, number] => {
        const l = leg(pose, side)
        return [Math.abs(l.ankle[0]) - p.hipWidth, l.ankle[2], Math.max(0, l.ankle[1] - p.ankleHeight)]
    }
    return {
        t,
        rise: pose.root[1] - (standingPelvisHeight(p) - 0.4),
        shiftX: pose.root[0],
        lean: pose.lean,
        twist: pose.twist,
        sideBend: pose.sideBend,
        headPitch: pose.headPitch,
        armL: arm(pose.arms.L),
        armR: arm(pose.arms.R),
        feetL: feet("L"),
        feetR: feet("R"),
    }
}

/**
 * A jump on the move, as a leap -- not the standing jump's two-footed spring. Picked up from the
 * stride a quarter round (`quarter` of LEAP_PHASES), the stride carries on into the push off the
 * foot that is down while the other knee drives forward; in the air the legs split, the lead one
 * reaching ahead and the trailing one stretched back, the arm opposite the lead leg forward and the
 * other back, the body still leaning into the run; at the hold the lead leg reaches down for the
 * ground; it lands on that foot where the stride would have put it, takes the weight in the knee,
 * and hands back to the stride where the legs are (`phaseAfter`). Timed as the game's jump is
 * (`timing`, the client lifting the body on its curve); `strength` sizes the split (a walk's leap is
 * a smaller one).
 */
export const leapFromStride = (
    gait: EmberClip,
    quarter: number,
    cyclesPerSecond: number,
    timing: JumpTiming,
    strength: number,
    p: Proportions,
    facing: string,
): StartPlan | undefined => {
    const at = (q: number) => gait.sample(((q % 1) + 1) % 1, p, facing)
    if (!allIk(at(0))) return undefined
    const phase0 = quarter / LEAP_PHASES
    const seconds = timing.ms / 1000
    const toPush = timing.takeoff * seconds * cyclesPerSecond
    const qOff = phase0 + toPush
    // The foot that strikes next after the push leads (the left strikes at 0, the right half way).
    const lead: Side = (((0 - qOff) % 1) + 1) % 1 < (((0.5 - qOff) % 1) + 1) % 1 ? "L" : "R"
    const trail = otherSide(lead)
    const strike = lead === "L" ? 0 : 0.5
    const settled = (1 - timing.landing) * seconds * cyclesPerSecond
    const phaseAfter = (strike + settled) % 1
    const k = strength
    const legKey = (side: Side, angles: [number, number, number] | [number, number, number, number, number]) =>
        side === "L" ? { legL: angles } : { legR: angles }
    const armKey = (side: Side, angles: [number, number, number]) => (side === "L" ? { armL: angles } : { armR: angles })
    const keys = new Map<Proportions, EmberKey[]>()
    const keysFor = (): EmberKey[] => {
        const start = keyOf(at(phase0), 0, p)
        const mid = keyOf(at(phase0 + toPush / 2), timing.takeoff / 2, p)
        // The push: the foot that is down stays down (or, already in the air, stretches back).
        const off = at(qOff)
        const push = keyOf(off, timing.takeoff, p)
        delete (push as any)[lead === "L" ? "feetL" : "feetR"]
        Object.assign(push, legKey(lead, [deg(48 * k + 8), deg(4), deg(80)]))
        if (!leg(off, trail).planted) {
            delete (push as any)[trail === "L" ? "feetL" : "feetR"]
            Object.assign(push, legKey(trail, [deg(-24 * k), deg(4), deg(25), 0, deg(40)]))
        }
        const lean = off.lean
        const top: EmberKey = {
            t: (timing.takeoff + timing.hold) / 2,
            lean: lean + deg(2),
            headPitch: off.headPitch,
            ...legKey(lead, [deg(60 * k + 6), deg(4), deg(30 + 20 * (1 - k)), 0, deg(15)]),
            ...legKey(trail, [deg(-32 * k), deg(4), deg(60), 0, deg(35)]),
            ...armKey(trail, [deg(62 * k + 10), deg(14), deg(78)]),
            ...armKey(lead, [deg(-40 * k), deg(12), deg(55)]),
        } as EmberKey
        const reach: EmberKey = {
            t: timing.hold,
            lean: lean + deg(1),
            headPitch: off.headPitch,
            ...legKey(lead, [deg(40 * k + 6), deg(4), deg(12), 0, deg(18)]),
            ...legKey(trail, [deg(6), deg(4), deg(80), 0, deg(30)]),
            ...armKey(trail, [deg(38 * k + 10), deg(16), deg(65)]),
            ...armKey(lead, [deg(-8 * k), deg(14), deg(55)]),
        } as EmberKey
        // Down on the lead foot where the stride strikes it, sunk into the knee; then the stride again.
        const landed = at(strike)
        const land = keyOf(landed, timing.landing, p)
        land.rise = (land.rise ?? 0) - 2.2 * k
        land.lean = (land.lean ?? 0) + deg(4)
        delete (land as any)[trail === "L" ? "feetL" : "feetR"]
        Object.assign(land, legKey(trail, [deg(-12 * k), deg(4), deg(70), 0, deg(30)]))
        const end = keyOf(at(phaseAfter), 1, p)
        return [start, mid, push, top, reach, land, end]
    }
    const sample = (t: number, body: Proportions, f?: string): PoseSample => {
        let k = keys.get(body)
        if (!k) keys.set(body, (k = keysFor()))
        return sampleKeyframes(k)(t, body, f)
    }
    return { clip: { name: "jump", frames: 12, frameMs: timing.ms / 12, loop: false, sample }, phaseAfter, cycles: 0 }
}
